import { useEffect, useState, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import {
  Package,
  FolderKanban,
  Layers,
  Ribbon,
  Settings,
  FileText,
  Plus,
  Search,
  MoreHorizontal,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  ImageIcon,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useMedals } from '../../hooks/useMedals';
import { MedalQuotesSection } from './medal-quotes';
import { useToast } from '../../contexts/ToastContext';
import type {
  MedalCategory,
  MedalMaterial,
  MedalProduct,
  MedalActiveTab,
  MedalQuote,
  MedalQuoteCustomItem,
} from '../../types/medals';

// Helper function to map bodyCount to display label
const getBodyCountLabel = (count: number): string => {
  switch (count) {
    case 1: return 'Base';
    case 2: return 'Base + Sobreposição';
    case 3: return 'Base + 2 Sobreposições';
    default: return `${count} corpos`;
  }
};

// Helper function to get individual layer label
const getLayerLabel = (layerIndex: number, totalLayers: number): string => {
  if (layerIndex === 1) return 'Base';
  if (totalLayers === 2) return 'Sobreposição';
  if (totalLayers === 3) {
    return layerIndex === 2 ? 'Sobreposição' : 'Sobreposição 2';
  }
  return `Corpo ${layerIndex}`;
};

interface MedalsPageProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  activeTab: MedalActiveTab;
  setActiveTab: (tab: MedalActiveTab) => void;
}

export function MedalsPage({
  accessToken,
  refreshToken,
  attemptRefresh,
  activeTab,
  setActiveTab,
}: MedalsPageProps) {
  const toast = useToast();
  const medals = useMedals({ accessToken, refreshToken, attemptRefresh, externalActiveTab: activeTab });

  // Local dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MedalCategory | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MedalProduct | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<MedalMaterial | null>(null);

  // Category form
  const [categoryCode, setCategoryCode] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryActive, setCategoryActive] = useState(true);
  const [categoryAllowedThicknesses, setCategoryAllowedThicknesses] = useState('');
  const [categoryDimensionConstraint, setCategoryDimensionConstraint] = useState('');

  // Material form
  const [materialCode, setMaterialCode] = useState('');
  const [materialName, setMaterialName] = useState('');
  const [materialDensity, setMaterialDensity] = useState('');
  const [materialPricePerKg, setMaterialPricePerKg] = useState('');
  const [materialAvailableThicknesses, setMaterialAvailableThicknesses] = useState('');
  const [materialActive, setMaterialActive] = useState(true);

  // Product form
  const [productCode, setProductCode] = useState('');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productCategoryId, setProductCategoryId] = useState('');
  const [productBaseShape, setProductBaseShape] = useState<'circular' | 'rectangular' | 'custom'>('circular');
  const [productBodyCount, setProductBodyCount] = useState('2');
  const [productPrintSides, setProductPrintSides] = useState('1');
  // Per-body material and thickness
  const [body1MaterialId, setBody1MaterialId] = useState('');
  const [body1Thickness, setBody1Thickness] = useState('');
  const [body2MaterialId, setBody2MaterialId] = useState('');
  const [body2Thickness, setBody2Thickness] = useState('');
  const [body3MaterialId, setBody3MaterialId] = useState('');
  const [body3Thickness, setBody3Thickness] = useState('');
  // Dimension for quoting (width x height)
  const [productDimensionId, setProductDimensionId] = useState<string | null>(null);
  const [productWidth, setProductWidth] = useState('');
  const [productHeight, setProductHeight] = useState('');
  const [productActive, setProductActive] = useState(true);
  const [productUseManualPricing, setProductUseManualPricing] = useState(false);
  const [productManualPrice, setProductManualPrice] = useState('');
  const [productIsSimpleProduct, setProductIsSimpleProduct] = useState(false);
  const [productSimpleThickness, setProductSimpleThickness] = useState('');
  const [productMinOrderQty, setProductMinOrderQty] = useState('1');
  const [productDisplayOrder, setProductDisplayOrder] = useState('0');
  // Image upload
  const [productImageUrl, setProductImageUrl] = useState('');
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ribbon Type form
  const [ribbonTypeDialogOpen, setRibbonTypeDialogOpen] = useState(false);
  const [selectedRibbonType, setSelectedRibbonType] = useState<{ id: string; code: string; name: string; description?: string; isPersonalized: boolean; active: boolean } | null>(null);
  const [ribbonTypeCode, setRibbonTypeCode] = useState('');
  const [ribbonTypeName, setRibbonTypeName] = useState('');
  const [ribbonTypeDescription, setRibbonTypeDescription] = useState('');
  const [ribbonTypeIsPersonalized, setRibbonTypeIsPersonalized] = useState(false);
  const [ribbonTypeActive, setRibbonTypeActive] = useState(true);

  // Ribbon Style form
  const [ribbonStyleDialogOpen, setRibbonStyleDialogOpen] = useState(false);
  const [selectedRibbonStyle, setSelectedRibbonStyle] = useState<{ id: string; code: string; name: string; description?: string; basePrice: number; active: boolean } | null>(null);
  const [ribbonStyleCode, setRibbonStyleCode] = useState('');
  const [ribbonStyleName, setRibbonStyleName] = useState('');
  const [ribbonStyleDescription, setRibbonStyleDescription] = useState('');
  const [ribbonStyleBasePrice, setRibbonStyleBasePrice] = useState('');
  const [ribbonStyleActive, setRibbonStyleActive] = useState(true);

  // Ribbon Color form
  const [ribbonColorDialogOpen, setRibbonColorDialogOpen] = useState(false);
  const [selectedRibbonColor, setSelectedRibbonColor] = useState<{ id: string; code: string; name: string; hexColor: string; priceAdjustment: number; active: boolean } | null>(null);
  const [ribbonColorCode, setRibbonColorCode] = useState('');
  const [ribbonColorName, setRibbonColorName] = useState('');
  const [ribbonColorHex, setRibbonColorHex] = useState('#000000');
  const [ribbonColorPriceAdjustment, setRibbonColorPriceAdjustment] = useState('0');
  const [ribbonColorActive, setRibbonColorActive] = useState(true);

  // Ribbon saving state
  const [ribbonSaving, setRibbonSaving] = useState(false);

  // Settings inputs (derived from medals.settings)
  const [settingsInputs, setSettingsInputs] = useState<Record<string, string>>({});
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Test calculator state
  const [testCalc, setTestCalc] = useState({
    widthCm: '',
    heightCm: '',
    thicknessMm: '',
    density: '',
    pricePerKg: '',
    quantity: '10',
    printSides: '1',
    bodyCount: '2',
    ribbonType: 'lisa',
  });

  // Helper to get setting value (from inputs or original)
  const getSettingValue = (key: string): string => {
    if (settingsInputs[key] !== undefined) return settingsInputs[key];
    const setting = medals.settings.find(s => s.key === key);
    return setting?.value || '';
  };

  // Calculate test price breakdown
  const calculateTestPrice = () => {
    const widthCm = parseFloat(testCalc.widthCm) || 5;
    const heightCm = parseFloat(testCalc.heightCm) || 5;
    const thicknessMm = parseFloat(testCalc.thicknessMm) || 3;
    const density = parseFloat(testCalc.density) || 1.19;
    const pricePerKg = parseFloat(testCalc.pricePerKg) || 45;
    const quantity = parseInt(testCalc.quantity) || 1;
    const printSides = parseInt(testCalc.printSides) || 1;
    const bodyCount = parseInt(testCalc.bodyCount) || 2;

    // Get settings values
    const materialFactor = parseFloat(getSettingValue('pricing.material_factor')) || 100;
    const cuttingMargin = parseFloat(getSettingValue('pricing.cutting_margin')) || 0.15;
    const uvPrintPerM2 = parseFloat(getSettingValue('pricing.uv_print_per_m2')) || 50;
    const ribbonPerUnit = parseFloat(getSettingValue('pricing.personalized_ribbon_per_unit')) || 5;
    const packagingPerUnit = parseFloat(getSettingValue('pricing.packaging_per_unit')) || 2;
    const adminCostPerOrder = parseFloat(getSettingValue('pricing.admin_cost_per_order')) || 10;
    const body3Adjustment = parseFloat(getSettingValue('pricing.body_3_adjustment')) || 1.20;

    // Margin tiers
    const tier1MaxQty = parseInt(getSettingValue('margin.tier_1_max_qty')) || 5;
    const tier1Rate = parseFloat(getSettingValue('margin.tier_1_rate')) || 0.35;
    const tier2MaxQty = parseInt(getSettingValue('margin.tier_2_max_qty')) || 9;
    const tier2Rate = parseFloat(getSettingValue('margin.tier_2_rate')) || 0.32;
    const tier3Rate = parseFloat(getSettingValue('margin.tier_3_rate')) || 0.30;

    // Determine margin rate based on quantity
    let marginRate = tier3Rate;
    if (quantity <= tier1MaxQty) marginRate = tier1Rate;
    else if (quantity <= tier2MaxQty) marginRate = tier2Rate;

    // Calculate material cost
    const widthM = widthCm / 100;
    const heightM = heightCm / 100;
    const thicknessM = thicknessMm / 1000;
    const volumeM3 = widthM * heightM * thicknessM;
    const weightKg = volumeM3 * density * 1000; // density is g/cm³
    const materialCost = weightKg * pricePerKg * materialFactor;

    // Cutting cost
    const cuttingCost = materialCost * cuttingMargin;

    // UV print cost
    const areaM2 = widthM * heightM;
    const uvCost = areaM2 * uvPrintPerM2 * printSides;

    // Body adjustment
    const bodyAdjustment = bodyCount === 3 ? body3Adjustment : 1.0;

    // Base unit cost
    const baseUnitCost = (materialCost + cuttingCost + uvCost) * bodyAdjustment;

    // Ribbon cost (for lisa type, assuming no extra cost)
    const ribbonCost = testCalc.ribbonType === 'personalizada' ? ribbonPerUnit : 0;

    // Total unit cost
    const unitCost = baseUnitCost + ribbonCost + packagingPerUnit;

    // Apply margin
    const unitPrice = unitCost / (1 - marginRate);

    // Total
    const subtotal = unitPrice * quantity;
    const total = subtotal + adminCostPerOrder;

    return {
      materialCost,
      cuttingCost,
      uvCost,
      bodyAdjustment,
      baseUnitCost,
      ribbonCost,
      packagingCost: packagingPerUnit,
      unitCost,
      marginRate,
      unitPrice,
      quantity,
      subtotal,
      adminCost: adminCostPerOrder,
      total,
    };
  };

  // Load data on tab change or filter change
  useEffect(() => {
    medals.fetchData();
  }, [activeTab, medals.fetchData]);

  // Cleanup preview URL when it changes or on component unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Handle tab change
  const handleTabChange = (tab: string) => {
    medals.setActiveTab(tab as MedalActiveTab);
  };

  // Category CRUD handlers
  const openCategoryDialog = (category?: MedalCategory) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryCode(category.code);
      setCategoryName(category.name);
      setCategoryDescription(category.description || '');
      setCategoryActive(category.active);
      setCategoryAllowedThicknesses(category.allowedThicknessesMm || '');
      setCategoryDimensionConstraint(category.dimensionConstraint || '');
    } else {
      setSelectedCategory(null);
      setCategoryCode('');
      setCategoryName('');
      setCategoryDescription('');
      setCategoryActive(true);
      setCategoryAllowedThicknesses('');
      setCategoryDimensionConstraint('');
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    const data = {
      code: categoryCode,
      name: categoryName,
      description: categoryDescription || undefined,
      active: categoryActive,
      allowedThicknessesMm: categoryAllowedThicknesses || undefined,
      dimensionConstraint: categoryDimensionConstraint || undefined,
    };

    let result;
    if (selectedCategory) {
      result = await medals.updateCategory(selectedCategory.id, data);
    } else {
      result = await medals.createCategory(data);
    }

    if (result.success) {
      toast.success(selectedCategory ? 'Categoria atualizada!' : 'Categoria criada!');
      setCategoryDialogOpen(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta categoria?')) return;
    const result = await medals.deleteCategory(id);
    if (result.success) {
      toast.success('Categoria excluída!');
    } else {
      toast.error(result.error);
    }
  };

  // Material CRUD handlers
  const openMaterialDialog = (material?: MedalMaterial) => {
    if (material) {
      setSelectedMaterial(material);
      setMaterialCode(material.code);
      setMaterialName(material.name);
      setMaterialDensity(String(material.density));
      setMaterialPricePerKg(String(material.pricePerKg));
      setMaterialAvailableThicknesses(material.availableThicknesses?.join(', ') || '2, 3, 4, 5, 6, 8, 10');
      setMaterialActive(material.active);
    } else {
      setSelectedMaterial(null);
      setMaterialCode('');
      setMaterialName('');
      setMaterialDensity('');
      setMaterialPricePerKg('');
      setMaterialAvailableThicknesses('2, 3, 4, 5, 6, 8, 10');
      setMaterialActive(true);
    }
    setMaterialDialogOpen(true);
  };

  const handleSaveMaterial = async () => {
    // Parse available thicknesses from comma-separated string
    const thicknesses = materialAvailableThicknesses
      .split(',')
      .map(s => parseFloat(s.trim()))
      .filter(n => !isNaN(n) && n > 0);

    const data = {
      code: materialCode,
      name: materialName,
      density: parseFloat(materialDensity),
      pricePerKg: parseFloat(materialPricePerKg),
      availableThicknesses: thicknesses.length > 0 ? thicknesses : undefined,
      active: materialActive,
    };

    let result;
    if (selectedMaterial) {
      result = await medals.updateMaterial(selectedMaterial.id, data);
    } else {
      result = await medals.createMaterial(data);
    }

    if (result.success) {
      toast.success(selectedMaterial ? 'Material atualizado!' : 'Material criado!');
      setMaterialDialogOpen(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleDeleteMaterial = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este material?')) return;
    const result = await medals.deleteMaterial(id);
    if (result.success) {
      toast.success('Material excluído!');
    } else {
      toast.error(result.error);
    }
  };

  // Product CRUD handlers
  const openProductDialog = (product?: MedalProduct) => {
    if (product) {
      setSelectedProduct(product);
      setProductCode(product.code);
      setProductName(product.name);
      setProductDescription(product.description || '');
      setProductCategoryId(product.categoryId || '');
      setProductBaseShape(product.baseShape);
      setProductBodyCount(String(product.bodyCount));
      setProductPrintSides(String(product.printSides));
      // Load per-body materials and thicknesses
      setBody1MaterialId(product.body1MaterialId || '');
      setBody1Thickness(product.body1Thickness ? String(product.body1Thickness) : '');
      setBody2MaterialId(product.body2MaterialId || '');
      setBody2Thickness(product.body2Thickness ? String(product.body2Thickness) : '');
      setBody3MaterialId(product.body3MaterialId || '');
      setBody3Thickness(product.body3Thickness ? String(product.body3Thickness) : '');
      // Load dimension if available
      const firstDim = product.dimensions?.[0];
      setProductDimensionId(firstDim?.id || null);
      setProductWidth(firstDim ? String(firstDim.widthCm) : '');
      setProductHeight(firstDim ? String(firstDim.heightCm) : '');
      setProductActive(product.active);
      setProductUseManualPricing(product.useManualPricing || false);
      setProductManualPrice(product.manualPrice ? String(product.manualPrice) : '');
      setProductIsSimpleProduct(product.isSimpleProduct || false);
      // For simple products, load thickness from dimension
      const dimThickness = firstDim ? String(firstDim.thicknessMm) : '';
      setProductSimpleThickness(product.isSimpleProduct ? dimThickness : '');
      setProductMinOrderQty(product.minOrderQty ? String(product.minOrderQty) : '1');
      setProductDisplayOrder(String(product.displayOrder || 0));
      setProductImageUrl(product.imageUrl || '');
    } else {
      setSelectedProduct(null);
      setProductCode('');
      setProductName('');
      setProductDescription('');
      setProductCategoryId('');
      setProductBaseShape('circular');
      setProductBodyCount('2');
      setProductPrintSides('1');
      setBody1MaterialId('');
      setBody1Thickness('');
      setBody2MaterialId('');
      setBody2Thickness('');
      setBody3MaterialId('');
      setBody3Thickness('');
      setProductDimensionId(null);
      setProductWidth('');
      setProductHeight('');
      setProductActive(true);
      setProductUseManualPricing(false);
      setProductManualPrice('');
      setProductIsSimpleProduct(false);
      setProductSimpleThickness('');
      setProductMinOrderQty('1');
      setProductDisplayOrder('0');
      setProductImageUrl('');
      setPendingImageFile(null);
      setPreviewUrl(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    setProductDialogOpen(true);
  };

  const handleProductImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedProduct) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg|webp|gif|avif)$/)) {
      toast.error('Formato de imagem inválido. Use PNG, JPG, WEBP, GIF ou AVIF.');
      return;
    }

    setImageUploading(true);

    try {
      const formData = new FormData();
      formData.append('file',file);

      const response = await fetch(`${API_BASE_URL}/backoffice/medals/products/${selectedProduct.id}/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Falha ao enviar imagem');
      }

      const payload = await response.json();
      const newImageUrl = payload.imageUrl || payload.data?.imageUrl;

      if (newImageUrl) {
        setProductImageUrl(newImageUrl);
        // Update the selected product to reflect the new image
        setSelectedProduct({ ...selectedProduct, imageUrl: newImageUrl });
        // Refresh products list to ensure all products show correct images
        await medals.fetchProducts();
        toast.success('Imagem enviada com sucesso!');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar imagem');
    } finally {
      setImageUploading(false);
    }
  };

  const handleProductImageDelete = async () => {
    if (!selectedProduct || !productImageUrl) return;

    setImageUploading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/medals/products/${selectedProduct.id}/image`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Falha ao remover imagem');
      }

      setProductImageUrl('');
      // Update the selected product to reflect the removal
      setSelectedProduct({ ...selectedProduct, imageUrl: undefined });
      toast.success('Imagem removida com sucesso!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao remover imagem');
    } finally {
      setImageUploading(false);
    }
  };

  const handlePendingImageSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg|webp|gif|avif)$/)) {
      toast.error('Formato de imagem inválido. Use PNG, JPG, WEBP, GIF ou AVIF.');
      return;
    }

    // Create new preview URL (old one is cleaned up by useEffect)
    setPreviewUrl(URL.createObjectURL(file));
    setPendingImageFile(file);
  }, [toast]);

  const handleRemovePendingImage = useCallback(() => {
    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Clear state (URL cleanup handled by useEffect)
    setPreviewUrl(null);
    setPendingImageFile(null);
  }, []);

  const handleSaveProduct = async () => {
    const bodyCount = parseInt(productBodyCount);

    // Build dimensions array if width and height are provided
    const thickness = productIsSimpleProduct ? productSimpleThickness : body1Thickness;
    const dimensions = (productWidth && productHeight && thickness) ? [{
      id: productDimensionId || undefined,  // Include ID for existing dimensions (proper PATCH)
      widthCm: parseFloat(productWidth),
      heightCm: parseFloat(productHeight),
      thicknessMm: parseFloat(thickness),
      displayLabel: `${productWidth}x${productHeight}cm`,
      active: true,
    }] : undefined;

    // Validation for simple products
    if (productIsSimpleProduct && !productManualPrice) {
      toast.error('Produtos simples devem ter preço manual definido');
      return;
    }

    const data = {
      code: productCode,
      name: productName,
      description: productDescription || undefined,
      categoryId: productCategoryId,
      baseShape: productBaseShape,
      bodyCount: productIsSimpleProduct ? 1 : bodyCount,
      printSides: productIsSimpleProduct ? 1 : parseInt(productPrintSides),
      // Per-body material configurations (optional for simple products)
      body1MaterialId: productIsSimpleProduct ? undefined : (body1MaterialId || undefined),
      body1Thickness: productIsSimpleProduct ? undefined : (body1Thickness ? parseFloat(body1Thickness) : undefined),
      body2MaterialId: productIsSimpleProduct ? undefined : (body2MaterialId || undefined),
      body2Thickness: productIsSimpleProduct ? undefined : (body2Thickness ? parseFloat(body2Thickness) : undefined),
      body3MaterialId: productIsSimpleProduct ? undefined : (bodyCount >= 3 && body3MaterialId ? body3MaterialId : undefined),
      body3Thickness: productIsSimpleProduct ? undefined : (bodyCount >= 3 && body3Thickness ? parseFloat(body3Thickness) : undefined),
      active: productActive,
      useManualPricing: productIsSimpleProduct ? true : productUseManualPricing,
      manualPrice: productManualPrice ? parseFloat(productManualPrice) : undefined,
      isSimpleProduct: productIsSimpleProduct,
      minOrderQty: productMinOrderQty ? parseInt(productMinOrderQty) : 1,
      displayOrder: parseInt(productDisplayOrder) || 0,
      dimensions: dimensions,
    };

    let result;
    if (selectedProduct) {
      result = await medals.updateProduct(selectedProduct.id, data);
    } else {
      result = await medals.createProduct(data);

      // If product was created successfully and there's a pending image, upload it
      if (result.success && pendingImageFile && result.data?.id) {
        const formData = new FormData();
        formData.append('file', pendingImageFile);

        try {
          const imageResponse = await fetch(`${API_BASE_URL}/backoffice/medals/products/${result.data.id}/image`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
            body: formData,
          });

          if (!imageResponse.ok) {
            const errorData = await imageResponse.json().catch(() => ({}));
            console.error('Failed to upload image:', errorData);
            toast.error('Produto criado, mas falha ao enviar imagem. Edite o produto para adicionar a imagem.');
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          toast.error('Produto criado, mas erro ao enviar imagem. Edite o produto para adicionar a imagem.');
        }
      }
    }

    if (result.success) {
      toast.success(selectedProduct ? 'Produto atualizado!' : 'Produto criado!');
      setProductDialogOpen(false);
      setPendingImageFile(null);
      setPreviewUrl(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refresh products list to get updated data including imageUrl
      await medals.fetchProducts();
    } else {
      toast.error(result.error);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este produto?')) return;
    const result = await medals.deleteProduct(id);
    if (result.success) {
      toast.success('Produto excluído!');
    } else {
      toast.error(result.error);
    }
  };

  const handleReorderProduct = async (productId: string, direction: 'up' | 'down') => {
    if (!accessToken) return;

    try {
      // Find the product and its group
      const product = medals.products.find(p => p.id === productId);
      if (!product) return;

      // Group products by baseShape (preserving current display order)
      const groupedProducts = [...medals.products.filter(p => p.baseShape === product.baseShape)];

      // Find the product's index in its group
      const currentIndex = groupedProducts.findIndex(p => p.id === productId);
      if (currentIndex === -1) return;

      // Determine the swap target
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= groupedProducts.length) return;

      // Swap positions in the array
      [groupedProducts[currentIndex], groupedProducts[targetIndex]] = [groupedProducts[targetIndex], groupedProducts[currentIndex]];

      // Assign sequential displayOrder to the entire group
      const items = groupedProducts.map((p, idx) => ({
        id: p.id,
        displayOrder: idx,
      }));

      // Call the reorder API
      const { reorderProducts } = await import('../../services/medals.service');
      await reorderProducts(accessToken, items);

      // Refresh the products list
      await medals.fetchProducts();
      toast.success('Ordem atualizada!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao reordenar produto');
    }
  };

  // Quote status handler
  const handleUpdateQuoteStatus = async (quote: MedalQuote, status: string) => {
    const result = await medals.updateQuoteStatus(quote.id, status);
    if (result.success) {
      toast.success('Status atualizado!');
    } else {
      toast.error(result.error);
    }
  };

  // Ribbon Type CRUD handlers
  const openRibbonTypeDialog = (type?: typeof selectedRibbonType) => {
    if (type) {
      setSelectedRibbonType(type);
      setRibbonTypeCode(type.code);
      setRibbonTypeName(type.name);
      setRibbonTypeDescription(type.description || '');
      setRibbonTypeIsPersonalized(type.isPersonalized);
      setRibbonTypeActive(type.active);
    } else {
      setSelectedRibbonType(null);
      setRibbonTypeCode('');
      setRibbonTypeName('');
      setRibbonTypeDescription('');
      setRibbonTypeIsPersonalized(false);
      setRibbonTypeActive(true);
    }
    setRibbonTypeDialogOpen(true);
  };

  const handleSaveRibbonType = async () => {
    if (!accessToken) return;
    setRibbonSaving(true);
    try {
      const data = {
        code: ribbonTypeCode,
        name: ribbonTypeName,
        description: ribbonTypeDescription || undefined,
        isPersonalized: ribbonTypeIsPersonalized,
        active: ribbonTypeActive,
      };
      const url = selectedRibbonType
        ? `/v1/backoffice/medals/ribbons/types/${selectedRibbonType.id}`
        : `/v1/backoffice/medals/ribbons/types`;
      const method = selectedRibbonType ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Erro ao salvar tipo de fita');

      toast.success(selectedRibbonType ? 'Tipo atualizado!' : 'Tipo criado!');
      setRibbonTypeDialogOpen(false);
      await medals.fetchRibbons();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setRibbonSaving(false);
    }
  };

  const handleDeleteRibbonType = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este tipo de fita?')) return;
    if (!accessToken) return;
    try {
      const res = await fetch(`/v1/backoffice/medals/ribbons/types/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Erro ao excluir tipo de fita');
      toast.success('Tipo excluído!');
      await medals.fetchRibbons();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  // Ribbon Style CRUD handlers
  const openRibbonStyleDialog = (style?: typeof selectedRibbonStyle) => {
    if (style) {
      setSelectedRibbonStyle(style);
      setRibbonStyleCode(style.code);
      setRibbonStyleName(style.name);
      setRibbonStyleDescription(style.description || '');
      setRibbonStyleBasePrice(String(style.basePrice));
      setRibbonStyleActive(style.active);
    } else {
      setSelectedRibbonStyle(null);
      setRibbonStyleCode('');
      setRibbonStyleName('');
      setRibbonStyleDescription('');
      setRibbonStyleBasePrice('');
      setRibbonStyleActive(true);
    }
    setRibbonStyleDialogOpen(true);
  };

  const handleSaveRibbonStyle = async () => {
    if (!accessToken) return;
    setRibbonSaving(true);
    try {
      const data = {
        code: ribbonStyleCode,
        name: ribbonStyleName,
        description: ribbonStyleDescription || undefined,
        basePrice: parseFloat(ribbonStyleBasePrice),
        active: ribbonStyleActive,
      };
      const url = selectedRibbonStyle
        ? `/v1/backoffice/medals/ribbons/styles/${selectedRibbonStyle.id}`
        : `/v1/backoffice/medals/ribbons/styles`;
      const method = selectedRibbonStyle ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Erro ao salvar estilo de fita');

      toast.success(selectedRibbonStyle ? 'Estilo atualizado!' : 'Estilo criado!');
      setRibbonStyleDialogOpen(false);
      await medals.fetchRibbons();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setRibbonSaving(false);
    }
  };

  const handleDeleteRibbonStyle = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este estilo de fita?')) return;
    if (!accessToken) return;
    try {
      const res = await fetch(`/v1/backoffice/medals/ribbons/styles/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Erro ao excluir estilo de fita');
      toast.success('Estilo excluído!');
      await medals.fetchRibbons();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  // Ribbon Color CRUD handlers
  const openRibbonColorDialog = (color?: typeof selectedRibbonColor) => {
    if (color) {
      setSelectedRibbonColor(color);
      setRibbonColorCode(color.code);
      setRibbonColorName(color.name);
      setRibbonColorHex(color.hexColor);
      setRibbonColorPriceAdjustment(String(color.priceAdjustment));
      setRibbonColorActive(color.active);
    } else {
      setSelectedRibbonColor(null);
      setRibbonColorCode('');
      setRibbonColorName('');
      setRibbonColorHex('#000000');
      setRibbonColorPriceAdjustment('0');
      setRibbonColorActive(true);
    }
    setRibbonColorDialogOpen(true);
  };

  const handleSaveRibbonColor = async () => {
    if (!accessToken) return;
    setRibbonSaving(true);
    try {
      const data = {
        code: ribbonColorCode,
        name: ribbonColorName,
        hexColor: ribbonColorHex,
        priceAdjustment: parseFloat(ribbonColorPriceAdjustment) || 0,
        active: ribbonColorActive,
      };
      const url = selectedRibbonColor
        ? `/v1/backoffice/medals/ribbons/colors/${selectedRibbonColor.id}`
        : `/v1/backoffice/medals/ribbons/colors`;
      const method = selectedRibbonColor ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Erro ao salvar cor de fita');

      toast.success(selectedRibbonColor ? 'Cor atualizada!' : 'Cor criada!');
      setRibbonColorDialogOpen(false);
      await medals.fetchRibbons();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setRibbonSaving(false);
    }
  };

  const handleDeleteRibbonColor = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta cor de fita?')) return;
    if (!accessToken) return;
    try {
      const res = await fetch(`/v1/backoffice/medals/ribbons/colors/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Erro ao excluir cor de fita');
      toast.success('Cor excluída!');
      await medals.fetchRibbons();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    if (!accessToken) return;
    setSettingsSaving(true);
    try {
      // Build settings map from inputs
      const settingsToSave: Record<string, string> = {};
      Object.entries(settingsInputs).forEach(([key, value]) => {
        if (value !== '') {
          settingsToSave[key] = value;
        }
      });

      if (Object.keys(settingsToSave).length === 0) {
        toast.error('Nenhuma alteração para salvar');
        setSettingsSaving(false);
        return;
      }

      // Call the API to save settings
      const response = await fetch(`/v1/backoffice/medals/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(settingsToSave),
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar configurações');
      }

      toast.success('Configurações salvas!');
      setSettingsInputs({});
      await medals.fetchSettings();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSettingsSaving(false);
    }
  };

  const testPrice = calculateTestPrice();

  // Group products by baseShape
  const groupProducts = () => {
    const grouped: Record<string, MedalProduct[]> = {
      circular: [],
      rectangular: [],
      custom: [],
    };

    medals.products.forEach((product) => {
      if (grouped[product.baseShape]) {
        grouped[product.baseShape].push(product);
      }
    });

    return grouped;
  };

  const getGroupLabel = (baseShape: string, count: number): string => {
    const labels: Record<string, string> = {
      circular: 'Medalhas Redondas',
      rectangular: 'Medalhas Retangulares',
      custom: 'Medalhas Personalizadas',
    };
    return `${labels[baseShape] || baseShape} (${count})`;
  };

  // Sidebar menu items
  const menuItems = [
    { id: 'products' as const, label: 'Produtos', icon: Package, badge: 0 },
    { id: 'categories' as const, label: 'Categorias', icon: FolderKanban, badge: 0 },
    { id: 'materials' as const, label: 'Materiais', icon: Layers, badge: 0 },
    { id: 'ribbons' as const, label: 'Fitas', icon: Ribbon, badge: 0 },
    { id: 'settings' as const, label: 'Configurações', icon: Settings, badge: 0 },
    { id: 'quotes' as const, label: 'Orçamentos', icon: FileText, badge: medals.pendingCustomItems.length },
  ];

  return (
    <>
        {/* Products */}
        {activeTab === 'products' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Produtos de Medalhas</CardTitle>
                  <CardDescription>
                    Produtos pré-configurados com dimensões fixas
                  </CardDescription>
                </div>
                <Button onClick={() => openProductDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Produto
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar produtos..."
                    value={medals.searchTerm}
                    onChange={(e) => medals.setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={medals.includeInactive}
                    onCheckedChange={medals.setIncludeInactive}
                  />
                  <Label className="text-sm">Incluir inativos</Label>
                </div>
              </div>

              {medals.loading ? (
                <div className="text-center py-8 text-muted-foreground">Carregando...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Posição</TableHead>
                      <TableHead className="w-[60px]">Imagem</TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Dimensões</TableHead>
                      <TableHead>Formato</TableHead>
                      <TableHead>Estrutura</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const grouped = groupProducts();
                      const groupOrder: Array<'circular' | 'rectangular' | 'custom'> = ['circular', 'rectangular', 'custom'];
                      const rows: JSX.Element[] = [];

                      groupOrder.forEach((baseShape) => {
                        const products = grouped[baseShape];
                        if (products.length === 0) return;

                        // Group header row
                        rows.push(
                          <TableRow key={`group-${baseShape}`} className="bg-muted/50 hover:bg-muted/50">
                            <TableCell colSpan={9} className="font-semibold">
                              {getGroupLabel(baseShape, products.length)}
                            </TableCell>
                          </TableRow>
                        );

                        // Product rows
                        products.forEach((product, index) => {
                          const isFirst = index === 0;
                          const isLast = index === products.length - 1;

                          rows.push(
                            <TableRow key={product.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground w-6">{index + 1}</span>
                                  <div className="flex flex-col gap-0.5">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5"
                                      disabled={isFirst}
                                      onClick={() => handleReorderProduct(product.id, 'up')}
                                    >
                                      <ChevronUp className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5"
                                      disabled={isLast}
                                      onClick={() => handleReorderProduct(product.id, 'down')}
                                    >
                                      <ChevronDown className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                {product.imageUrl ? (
                                  <img
                                    key={`${product.id}-${product.imageUrl}`}
                                    src={`${product.imageUrl}?t=${Date.now()}`}
                                    alt={product.name}
                                    className="h-9 w-9 rounded object-cover"
                                  />
                                ) : (
                                  <div className="h-9 w-9 rounded bg-muted flex items-center justify-center">
                                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="font-mono">{product.code}</TableCell>
                              <TableCell>{product.name}</TableCell>
                              <TableCell>{product.dimensions?.[0]?.displayLabel || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {product.baseShape === 'circular' ? 'Redondo' :
                                   product.baseShape === 'rectangular' ? 'Retangular' : 'Personalizado'}
                                </Badge>
                              </TableCell>
                              <TableCell>{getBodyCountLabel(product.bodyCount)}</TableCell>
                              <TableCell>
                                <button
                                  onClick={() => medals.toggleProductStatus(product.id)}
                                  className="hover:opacity-80 transition-opacity"
                                  title="Clique para alternar status"
                                >
                                  <Badge variant={product.active ? 'default' : 'secondary'} className="cursor-pointer">
                                    {product.active ? 'Ativo' : 'Inativo'}
                                  </Badge>
                                </button>
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openProductDialog(product)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Editar
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteProduct(product.id)}
                                      className="text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Excluir
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      });

                      if (rows.length === 0) {
                        rows.push(
                          <TableRow key="empty">
                            <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                              Nenhum produto encontrado
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return rows;
                    })()}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Categories */}
        {activeTab === 'categories' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Categorias</CardTitle>
                  <CardDescription>
                    Categorias de medalhas: Redondo, Retangular, Personalizado
                  </CardDescription>
                </div>
                <Button onClick={() => openCategoryDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Categoria
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {medals.loading ? (
                <div className="text-center py-8 text-muted-foreground">Carregando...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {medals.categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell className="font-mono">{category.code}</TableCell>
                        <TableCell>{category.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {category.description || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={category.active ? 'default' : 'secondary'}>
                            {category.active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openCategoryDialog(category)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteCategory(category.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Materials */}
        {activeTab === 'materials' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Materiais</CardTitle>
                  <CardDescription>
                    Materiais acrílicos para fabricação de medalhas
                  </CardDescription>
                </div>
                <Button onClick={() => openMaterialDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Material
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {medals.loading ? (
                <div className="text-center py-8 text-muted-foreground">Carregando...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Densidade (kg/m³)</TableHead>
                      <TableHead>Preço/Kg (R$)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {medals.materials.map((material) => (
                      <TableRow key={material.id}>
                        <TableCell className="font-mono">{material.code}</TableCell>
                        <TableCell>{material.name}</TableCell>
                        <TableCell>{material.density}</TableCell>
                        <TableCell>R$ {material.pricePerKg.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={material.active ? 'default' : 'secondary'}>
                            {material.active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openMaterialDialog(material)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteMaterial(material.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                    {medals.materials.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          Nenhum material encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ribbons */}
        {activeTab === 'ribbons' && (
          <div className="grid gap-6">
            {/* Tipos de Fita */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Tipos de Fita</CardTitle>
                    <CardDescription>Lisa ou Personalizada</CardDescription>
                  </div>
                  <Button onClick={() => openRibbonTypeDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Tipo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {medals.loading ? (
                  <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Personalizada</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {medals.ribbonTypes.map((type) => (
                        <TableRow key={type.id}>
                          <TableCell className="font-mono">{type.code}</TableCell>
                          <TableCell>{type.name}</TableCell>
                          <TableCell>
                            {type.isPersonalized ? (
                              <Badge>Sim</Badge>
                            ) : (
                              <Badge variant="outline">Não</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={type.active ? 'default' : 'secondary'}>
                              {type.active ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openRibbonTypeDialog(type)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteRibbonType(type.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {medals.ribbonTypes.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            Nenhum tipo de fita cadastrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Estilos de Fita */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Estilos de Fita</CardTitle>
                    <CardDescription>Simples Cordão, Premium Cordão, etc.</CardDescription>
                  </div>
                  <Button onClick={() => openRibbonStyleDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Estilo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {medals.loading ? (
                  <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Preço Base (R$)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {medals.ribbonStyles.map((style) => (
                        <TableRow key={style.id}>
                          <TableCell className="font-mono">{style.code}</TableCell>
                          <TableCell>{style.name}</TableCell>
                          <TableCell>R$ {style.basePrice.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant={style.active ? 'default' : 'secondary'}>
                              {style.active ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openRibbonStyleDialog(style)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteRibbonStyle(style.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {medals.ribbonStyles.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            Nenhum estilo de fita cadastrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Cores de Fita */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Cores de Fita</CardTitle>
                    <CardDescription>Cores disponíveis para fitas lisas</CardDescription>
                  </div>
                  <Button onClick={() => openRibbonColorDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Cor
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {medals.loading ? (
                  <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cor</TableHead>
                        <TableHead>Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Ajuste Preço (R$)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {medals.ribbonColors.map((color) => (
                        <TableRow key={color.id}>
                          <TableCell>
                            <div
                              className="w-6 h-6 rounded border"
                              style={{ backgroundColor: color.hexColor }}
                            />
                          </TableCell>
                          <TableCell className="font-mono">{color.code}</TableCell>
                          <TableCell>{color.name}</TableCell>
                          <TableCell>
                            {color.priceAdjustment > 0 ? `+R$ ${color.priceAdjustment.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={color.active ? 'default' : 'secondary'}>
                              {color.active ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openRibbonColorDialog(color)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteRibbonColor(color.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {medals.ribbonColors.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            Nenhuma cor de fita cadastrada
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <Card>
            <CardHeader>
              <CardTitle>Configurações de Precificação</CardTitle>
              <CardDescription>
                Configure os custos e margens da fórmula de preço de medalhas.
                <br />
                <span className="font-medium">Fórmula:</span> Preço = (Material + Corte + UV + Ajuste Camadas) / (1 - Margem) + Fita + Embalagem
              </CardDescription>
            </CardHeader>
            <CardContent>
              {medals.loading ? (
                <div className="text-center py-8 text-muted-foreground">Carregando...</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column: Settings */}
                  <div className="space-y-6">
                    {/* Custos Base */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Custos Base</h3>

                      <div className="space-y-2">
                        <Label htmlFor="material_factor">Fator de Material</Label>
                        <Input
                          id="material_factor"
                          type="number"
                          step="1"
                          value={settingsInputs['pricing.material_factor'] ?? getSettingValue('pricing.material_factor')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'pricing.material_factor': e.target.value }))}
                          placeholder="100"
                        />
                        <p className="text-xs text-muted-foreground">
                          Multiplicador do custo de material. Valor atual: {getSettingValue('pricing.material_factor') || '100'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="cutting_margin">Margem de Corte (%)</Label>
                        <Input
                          id="cutting_margin"
                          type="number"
                          step="0.01"
                          value={settingsInputs['pricing.cutting_margin'] ?? getSettingValue('pricing.cutting_margin')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'pricing.cutting_margin': e.target.value }))}
                          placeholder="0.15"
                        />
                        <p className="text-xs text-muted-foreground">
                          Percentual adicional para corte (0.15 = 15%). Atual: {(parseFloat(getSettingValue('pricing.cutting_margin')) * 100 || 15).toFixed(0)}%
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="uv_print">Custo Impressão UV por m² (R$)</Label>
                        <Input
                          id="uv_print"
                          type="number"
                          step="0.01"
                          value={settingsInputs['pricing.uv_print_per_m2'] ?? getSettingValue('pricing.uv_print_per_m2')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'pricing.uv_print_per_m2': e.target.value }))}
                          placeholder="50.00"
                        />
                        <p className="text-xs text-muted-foreground">
                          Custo de impressão UV por metro quadrado. Atual: R$ {getSettingValue('pricing.uv_print_per_m2') || '50.00'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="body_3">Ajuste 3 Camadas</Label>
                        <Input
                          id="body_3"
                          type="number"
                          step="0.01"
                          value={settingsInputs['pricing.body_3_adjustment'] ?? getSettingValue('pricing.body_3_adjustment')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'pricing.body_3_adjustment': e.target.value }))}
                          placeholder="1.20"
                        />
                        <p className="text-xs text-muted-foreground">
                          Multiplicador para medalhas de 3 camadas (1.20 = +20%). Atual: {getSettingValue('pricing.body_3_adjustment') || '1.20'}
                        </p>
                      </div>
                    </div>

                    {/* Custos Adicionais */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Custos Adicionais por Unidade</h3>

                      <div className="space-y-2">
                        <Label htmlFor="ribbon_cost">Fita Personalizada (R$/un)</Label>
                        <Input
                          id="ribbon_cost"
                          type="number"
                          step="0.01"
                          value={settingsInputs['pricing.personalized_ribbon_per_unit'] ?? getSettingValue('pricing.personalized_ribbon_per_unit')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'pricing.personalized_ribbon_per_unit': e.target.value }))}
                          placeholder="5.00"
                        />
                        <p className="text-xs text-muted-foreground">
                          Custo adicional para fita personalizada. Atual: R$ {getSettingValue('pricing.personalized_ribbon_per_unit') || '5.00'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="packaging">Embalagem (R$/un)</Label>
                        <Input
                          id="packaging"
                          type="number"
                          step="0.01"
                          value={settingsInputs['pricing.packaging_per_unit'] ?? getSettingValue('pricing.packaging_per_unit')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'pricing.packaging_per_unit': e.target.value }))}
                          placeholder="2.00"
                        />
                        <p className="text-xs text-muted-foreground">
                          Custo de embalagem por unidade. Atual: R$ {getSettingValue('pricing.packaging_per_unit') || '2.00'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="admin_cost">Custo Administrativo (R$/pedido)</Label>
                        <Input
                          id="admin_cost"
                          type="number"
                          step="0.01"
                          value={settingsInputs['pricing.admin_cost_per_order'] ?? getSettingValue('pricing.admin_cost_per_order')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'pricing.admin_cost_per_order': e.target.value }))}
                          placeholder="10.00"
                        />
                        <p className="text-xs text-muted-foreground">
                          Custo fixo por pedido. Atual: R$ {getSettingValue('pricing.admin_cost_per_order') || '10.00'}
                        </p>
                      </div>
                    </div>

                    {/* Faixas de Margem */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Faixas de Margem</h3>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="tier1_qty">Tier 1: Até (qtd)</Label>
                          <Input
                            id="tier1_qty"
                            type="number"
                            value={settingsInputs['margin.tier_1_max_qty'] ?? getSettingValue('margin.tier_1_max_qty')}
                            onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'margin.tier_1_max_qty': e.target.value }))}
                            placeholder="5"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="tier1_rate">Margem Tier 1</Label>
                          <Input
                            id="tier1_rate"
                            type="number"
                            step="0.01"
                            value={settingsInputs['margin.tier_1_rate'] ?? getSettingValue('margin.tier_1_rate')}
                            onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'margin.tier_1_rate': e.target.value }))}
                            placeholder="0.35"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        1-{getSettingValue('margin.tier_1_max_qty') || '5'} unidades: {(parseFloat(getSettingValue('margin.tier_1_rate')) * 100 || 35).toFixed(0)}% de margem
                      </p>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="tier2_qty">Tier 2: Até (qtd)</Label>
                          <Input
                            id="tier2_qty"
                            type="number"
                            value={settingsInputs['margin.tier_2_max_qty'] ?? getSettingValue('margin.tier_2_max_qty')}
                            onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'margin.tier_2_max_qty': e.target.value }))}
                            placeholder="9"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="tier2_rate">Margem Tier 2</Label>
                          <Input
                            id="tier2_rate"
                            type="number"
                            step="0.01"
                            value={settingsInputs['margin.tier_2_rate'] ?? getSettingValue('margin.tier_2_rate')}
                            onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'margin.tier_2_rate': e.target.value }))}
                            placeholder="0.32"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {parseInt(getSettingValue('margin.tier_1_max_qty')) + 1 || 6}-{getSettingValue('margin.tier_2_max_qty') || '9'} unidades: {(parseFloat(getSettingValue('margin.tier_2_rate')) * 100 || 32).toFixed(0)}% de margem
                      </p>

                      <div className="space-y-2">
                        <Label htmlFor="tier3_rate">Margem Tier 3 (10+ unidades)</Label>
                        <Input
                          id="tier3_rate"
                          type="number"
                          step="0.01"
                          value={settingsInputs['margin.tier_3_rate'] ?? getSettingValue('margin.tier_3_rate')}
                          onChange={(e) => setSettingsInputs(prev => ({ ...prev, 'margin.tier_3_rate': e.target.value }))}
                          placeholder="0.30"
                        />
                        <p className="text-xs text-muted-foreground">
                          10+ unidades: {(parseFloat(getSettingValue('margin.tier_3_rate')) * 100 || 30).toFixed(0)}% de margem
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={handleSaveSettings}
                      disabled={settingsSaving || Object.keys(settingsInputs).length === 0}
                      className="w-full"
                    >
                      {settingsSaving ? 'Salvando...' : 'Salvar Todas as Configurações'}
                    </Button>
                  </div>

                  {/* Right Column: Test Calculator */}
                  <div className="space-y-4">
                    <div className="sticky top-20">
                      <div className="bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
                        <h4 className="font-semibold mb-3">Calculadora de Teste</h4>
                        <p className="text-xs text-muted-foreground mb-4">
                          Teste como as configurações afetam o preço final.
                        </p>

                        <div className="space-y-3 mb-4">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Largura (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={testCalc.widthCm}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, widthCm: e.target.value }))}
                                placeholder="5"
                                className="mt-1 h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Altura (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={testCalc.heightCm}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, heightCm: e.target.value }))}
                                placeholder="5"
                                className="mt-1 h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Espessura (mm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={testCalc.thicknessMm}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, thicknessMm: e.target.value }))}
                                placeholder="3"
                                className="mt-1 h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Densidade (g/cm³)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={testCalc.density}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, density: e.target.value }))}
                                placeholder="1.19"
                                className="mt-1 h-8"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs">Preço por Kg (R$)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={testCalc.pricePerKg}
                              onChange={(e) => setTestCalc(prev => ({ ...prev, pricePerKg: e.target.value }))}
                              placeholder="45.00"
                              className="mt-1 h-8"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs">Quantidade</Label>
                              <Input
                                type="number"
                                value={testCalc.quantity}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, quantity: e.target.value }))}
                                placeholder="10"
                                className="mt-1 h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Lados Imp.</Label>
                              <Select value={testCalc.printSides} onValueChange={(v) => setTestCalc(prev => ({ ...prev, printSides: v }))}>
                                <SelectTrigger className="mt-1 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1 lado</SelectItem>
                                  <SelectItem value="2">2 lados</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Estrutura</Label>
                              <Select value={testCalc.bodyCount} onValueChange={(v) => setTestCalc(prev => ({ ...prev, bodyCount: v }))}>
                                <SelectTrigger className="mt-1 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">Base</SelectItem>
                                  <SelectItem value="2">Base + Sobreposição</SelectItem>
                                  <SelectItem value="3">Base + 2 Sobreposições</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs">Tipo de Fita</Label>
                            <Select value={testCalc.ribbonType} onValueChange={(v) => setTestCalc(prev => ({ ...prev, ribbonType: v }))}>
                              <SelectTrigger className="mt-1 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="lisa">Lisa (incluso)</SelectItem>
                                <SelectItem value="personalizada">Personalizada (+R$)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="border-t border-blue-200 dark:border-blue-800 pt-4 space-y-2">
                          <h5 className="font-medium text-sm mb-3">Breakdown de Custos</h5>

                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Custo Material:</span>
                              <span className="font-medium">R$ {testPrice.materialCost.toFixed(4)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Custo Corte ({(parseFloat(getSettingValue('pricing.cutting_margin')) * 100 || 15).toFixed(0)}%):</span>
                              <span className="font-medium">+ R$ {testPrice.cuttingCost.toFixed(4)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Impressão UV ({testCalc.printSides} lado{testCalc.printSides === '2' ? 's' : ''}):</span>
                              <span className="font-medium">+ R$ {testPrice.uvCost.toFixed(4)}</span>
                            </div>
                            {testCalc.bodyCount === '3' && (
                              <div className="flex justify-between text-amber-600">
                                <span>Ajuste 3 camadas (×{testPrice.bodyAdjustment}):</span>
                                <span className="font-medium">aplicado</span>
                              </div>
                            )}
                            <div className="flex justify-between border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                              <span className="font-medium">Custo Base Unitário:</span>
                              <span className="font-semibold">R$ {testPrice.baseUnitCost.toFixed(2)}</span>
                            </div>
                          </div>

                          <div className="border-t border-blue-200 dark:border-blue-800 pt-3 mt-3 space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">+ Fita {testCalc.ribbonType}:</span>
                              <span className="font-medium">R$ {testPrice.ribbonCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">+ Embalagem:</span>
                              <span className="font-medium">R$ {testPrice.packagingCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                              <span className="font-medium">Custo Total Unitário:</span>
                              <span className="font-semibold">R$ {testPrice.unitCost.toFixed(2)}</span>
                            </div>
                          </div>

                          <div className="border-t border-blue-200 dark:border-blue-800 pt-3 mt-3 space-y-1 text-xs">
                            <div className="flex justify-between text-green-600 dark:text-green-400">
                              <span>Margem ({(testPrice.marginRate * 100).toFixed(0)}% - {testPrice.quantity <= parseInt(getSettingValue('margin.tier_1_max_qty') || '5') ? 'Tier 1' : testPrice.quantity <= parseInt(getSettingValue('margin.tier_2_max_qty') || '9') ? 'Tier 2' : 'Tier 3'}):</span>
                              <span className="font-medium">aplicada</span>
                            </div>
                            <div className="flex justify-between border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                              <span className="font-medium">Preço Unitário:</span>
                              <span className="font-semibold text-blue-600 dark:text-blue-400">R$ {testPrice.unitPrice.toFixed(2)}</span>
                            </div>
                          </div>

                          <div className="border-t-2 border-blue-300 dark:border-blue-700 pt-3 mt-3">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Subtotal ({testPrice.quantity}× R$ {testPrice.unitPrice.toFixed(2)}):</span>
                              <span className="font-medium">R$ {testPrice.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">+ Custo Admin:</span>
                              <span className="font-medium">R$ {testPrice.adminCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <span className="font-semibold">Total do Pedido:</span>
                              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">R$ {testPrice.total.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quotes */}
        {activeTab === 'quotes' && (
          <div className="space-y-6">
            <MedalQuotesSection
              quotes={medals.quotes}
              quotesLoading={medals.loading}
              accessToken={accessToken}
              materials={medals.materials}
              products={medals.products}
              onRefresh={async () => {
                await medals.fetchQuotes();
              }}
              onUpdateStatus={async (quoteId: string, status: string) => {
                const result = await medals.updateQuoteStatus(quoteId, status);
                if (result.success) {
                  toast.success('Status atualizado!');
                } else {
                  toast.error(result.error);
                }
              }}
              onUpdateQuoteItem={medals.updateQuoteItem}
              onDeleteQuoteItem={medals.deleteQuoteItem}
              onAddQuoteItem={medals.addQuoteItem}
              onMarkAsViewed={async (quoteId: string) => {
                const result = await medals.markMedalQuoteAsViewed(quoteId);
                if (!result.success && result.error) {
                  toast.error(result.error);
                }
              }}
            />
          </div>
        )}

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da categoria de medalhas
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="cat-code">Código</Label>
              <Input
                id="cat-code"
                value={categoryCode}
                onChange={(e) => setCategoryCode(e.target.value)}
                placeholder="REDONDO"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cat-name">Nome</Label>
              <Input
                id="cat-name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Medalha Redonda"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cat-desc">Descrição</Label>
              <Input
                id="cat-desc"
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                placeholder="Descrição da categoria"
              />
            </div>
            {/* Only show constraints for Personalizada category */}
            {(categoryCode.toUpperCase().includes('PERSONAL') || categoryCode.toUpperCase().includes('CUSTOM')) && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="cat-allowed-thicknesses">
                    Espessuras Permitidas (opcional)
                  </Label>
                  <Input
                    id="cat-allowed-thicknesses"
                    placeholder="Ex: 2,3,4,6,8"
                    value={categoryAllowedThicknesses}
                    onChange={(e) => setCategoryAllowedThicknesses(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Separadas por vírgula. Define as espessuras disponíveis no formulário.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="cat-dimension-constraint">
                    Restrição de Dimensões (opcional)
                  </Label>
                  <Input
                    id="cat-dimension-constraint"
                    placeholder="Ex: <15x<15, 15x15, >15x>15, <10x<17"
                    value={categoryDimensionConstraint}
                    onChange={(e) => setCategoryDimensionConstraint(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Formato: largura x altura com operadores &lt;, &gt; ou exato
                  </p>
                </div>
              </>
            )}
            <div className="flex items-center gap-2">
              <Switch
                checked={categoryActive}
                onCheckedChange={setCategoryActive}
              />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveCategory} disabled={medals.saving}>
              {medals.saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
            <DialogDescription>
              Configure o produto de medalha
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Product Type Selector - Only for new products */}
            {!selectedProduct && (
              <div className="grid gap-2 pb-4 mb-4 border-b">
                <Label>Tipo de Produto</Label>
                <Select
                  value={productIsSimpleProduct ? "simple" : "regular"}
                  onValueChange={(v) => setProductIsSimpleProduct(v === "simple")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Produto (configuração completa)</SelectItem>
                    <SelectItem value="simple">Produto Simples (sem configuração de materiais)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Produtos simples usam preço manual. Cliente escolhe tamanho e quantidade.
                </p>
              </div>
            )}

            {/* Show product type badge when editing */}
            {selectedProduct && (
              <div className="pb-4 mb-4 border-b">
                <Badge variant={productIsSimpleProduct ? "secondary" : "default"}>
                  {productIsSimpleProduct ? "Produto Simples" : "Produto Regular"}
                </Badge>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="prod-code">Código</Label>
                <Input
                  id="prod-code"
                  value={productCode}
                  onChange={(e) => setProductCode(e.target.value)}
                  placeholder="MDL-RED-001"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="prod-name">Nome</Label>
                <Input
                  id="prod-name"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Medalha Redonda Cristal"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="prod-desc">Descrição</Label>
              <Textarea
                id="prod-desc"
                value={productDescription}
                onChange={(e) => setProductDescription(e.target.value)}
                placeholder="Descrição do produto"
                rows={3}
                className="resize-none overflow-y-auto"
              />
            </div>

            {/* Image Upload Section */}
            <div className="grid gap-2">
              <Label>Imagem do Produto</Label>
              {!selectedProduct ? (
                pendingImageFile && previewUrl ? (
                  <div className="rounded-lg border border-border bg-card overflow-hidden">
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <div className="p-3 flex justify-between items-center">
                      <span className="text-sm text-muted-foreground truncate flex-1 mr-2">
                        {pendingImageFile.name}
                      </span>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleRemovePendingImage}
                      >
                        Remover
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-muted-foreground/25 bg-muted/20 p-6 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Package className="h-8 w-8 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground mb-2">
                        Escolha uma imagem (opcional)
                      </p>
                      <label htmlFor="new-product-image-upload">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('new-product-image-upload')?.click()}
                        >
                          Escolher imagem
                        </Button>
                      </label>
                      <input
                        ref={fileInputRef}
                        id="new-product-image-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/avif"
                        className="hidden"
                        onChange={handlePendingImageSelect}
                      />
                      <p className="text-xs text-muted-foreground">
                        PNG, JPG, WEBP, GIF ou AVIF
                      </p>
                    </div>
                  </div>
                )
              ) : productImageUrl ? (
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <div className="relative bg-gradient-to-b from-[#151515] to-[#0d0d0d]">
                    <img
                      key={productImageUrl}
                      src={`${productImageUrl}?t=${Date.now()}`}
                      alt={productName || 'Produto'}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        console.error('Failed to load image:', productImageUrl);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="p-3 flex justify-between items-center bg-card">
                    <div className="flex-1 mr-2 overflow-hidden">
                      <div className="text-sm text-muted-foreground truncate">
                        {productImageUrl.split('/').pop()}
                      </div>
                      <div className="text-xs text-muted-foreground/60 truncate mt-0.5">
                        {productImageUrl}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleProductImageDelete}
                      disabled={imageUploading}
                    >
                      {imageUploading ? 'Removendo...' : 'Remover imagem'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-muted-foreground/25 bg-muted/20 p-6 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Package className="h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground mb-2">
                      Nenhuma imagem carregada
                    </p>
                    <label htmlFor="product-image-upload">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={imageUploading}
                        onClick={() => document.getElementById('product-image-upload')?.click()}
                      >
                        {imageUploading ? 'Enviando...' : 'Escolher imagem'}
                      </Button>
                    </label>
                    <input
                      id="product-image-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/avif"
                      className="hidden"
                      onChange={handleProductImageUpload}
                    />
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, WEBP, GIF ou AVIF
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label>Categoria</Label>
              <Select value={productCategoryId} onValueChange={setProductCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {medals.categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dimensões base — shown for ALL product types */}
            <div className={`grid ${productIsSimpleProduct ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
              <div className="grid gap-2">
                <Label htmlFor="prod-width">Largura (cm)</Label>
                <Input
                  id="prod-width"
                  type="number"
                  step="0.1"
                  min="0"
                  value={productWidth}
                  onChange={(e) => setProductWidth(e.target.value)}
                  placeholder="5"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="prod-height">Altura (cm)</Label>
                <Input
                  id="prod-height"
                  type="number"
                  step="0.1"
                  min="0"
                  value={productHeight}
                  onChange={(e) => setProductHeight(e.target.value)}
                  placeholder="5"
                />
              </div>
              {productIsSimpleProduct && (
                <div className="grid gap-2">
                  <Label htmlFor="prod-simple-thickness">Espessura (mm)</Label>
                  <Input
                    id="prod-simple-thickness"
                    type="number"
                    step="0.1"
                    min="0"
                    value={productSimpleThickness}
                    onChange={(e) => setProductSimpleThickness(e.target.value)}
                    placeholder="2"
                  />
                </div>
              )}
            </div>

            {/* Regular product configuration - Hidden for simple products */}
            {!productIsSimpleProduct && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label>Formato</Label>
                    <Select value={productBaseShape} onValueChange={(v) => setProductBaseShape(v as 'circular' | 'rectangular' | 'custom')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circular">Redondo</SelectItem>
                        <SelectItem value="rectangular">Retangular</SelectItem>
                        <SelectItem value="custom">Personalizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Estrutura</Label>
                <Select value={productBodyCount} onValueChange={setProductBodyCount}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Base</SelectItem>
                    <SelectItem value="2">Base + Sobreposição</SelectItem>
                    <SelectItem value="3">Base + 2 Sobreposições</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Lados Impressão</Label>
                <Select value={productPrintSides} onValueChange={setProductPrintSides}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 lado</SelectItem>
                    <SelectItem value="2">2 lados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Per-body material and thickness configuration */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-sm">Configuração por Camada</h4>

              {/* Body 1 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Base - Material</Label>
                  <Select value={body1MaterialId} onValueChange={setBody1MaterialId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o material..." />
                    </SelectTrigger>
                    <SelectContent>
                      {medals.materials.map((mat) => (
                        <SelectItem key={mat.id} value={mat.id}>
                          {mat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Base - Espessura (mm)</Label>
                  <Select value={body1Thickness} onValueChange={setBody1Thickness}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(medals.materials.find(m => m.id === body1MaterialId)?.availableThicknesses || [2, 3, 4, 5, 6, 8, 10]).map((t) => (
                        <SelectItem key={t} value={String(t)}>
                          {t} mm
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Body 2 - only shown for 2+ body medals */}
              {(productBodyCount === '2' || productBodyCount === '3') && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Sobreposição - Material</Label>
                    <Select value={body2MaterialId} onValueChange={setBody2MaterialId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o material..." />
                      </SelectTrigger>
                      <SelectContent>
                        {medals.materials.map((mat) => (
                          <SelectItem key={mat.id} value={mat.id}>
                            {mat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Sobreposição - Espessura (mm)</Label>
                    <Select value={body2Thickness} onValueChange={setBody2Thickness}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(medals.materials.find(m => m.id === body2MaterialId)?.availableThicknesses || [2, 3, 4, 5, 6, 8, 10]).map((t) => (
                          <SelectItem key={t} value={String(t)}>
                            {t} mm
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Body 3 - only shown for 3-body medals */}
              {productBodyCount === '3' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Sobreposição 2 - Material</Label>
                    <Select value={body3MaterialId} onValueChange={setBody3MaterialId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o material..." />
                      </SelectTrigger>
                      <SelectContent>
                        {medals.materials.map((mat) => (
                          <SelectItem key={mat.id} value={mat.id}>
                            {mat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Sobreposição 2 - Espessura (mm)</Label>
                    <Select value={body3Thickness} onValueChange={setBody3Thickness}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(medals.materials.find(m => m.id === body3MaterialId)?.availableThicknesses || [2, 3, 4, 5, 6, 8, 10]).map((t) => (
                          <SelectItem key={t} value={String(t)}>
                            {t} mm
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
              </>
            )}

            {/* Simple Product Notice */}
            {productIsSimpleProduct && (
              <div className="border rounded-lg p-4 bg-muted/50">
                <p className="text-sm text-muted-foreground">
                  <strong>Produto Simples:</strong> Materiais e fitas não são necessários.
                  Preencha as dimensões acima para o cliente escolher o tamanho.
                  O preço manual é obrigatório.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="prod-display-order">Posição na vitrine</Label>
                <Input
                  id="prod-display-order"
                  type="number"
                  min="0"
                  value={productDisplayOrder}
                  onChange={(e) => setProductDisplayOrder(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="prod-min-order">Pedido Mínimo</Label>
                <Input
                  id="prod-min-order"
                  type="number"
                  min="1"
                  step="1"
                  value={productMinOrderQty}
                  onChange={(e) => setProductMinOrderQty(e.target.value)}
                  placeholder="1"
                />
                <p className="text-xs text-muted-foreground">
                  Quantidade mínima que o cliente deve pedir. Padrão: 1 unidade.
                </p>
              </div>
            </div>

            {/* Manual Pricing Checkbox */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="prod-manual-pricing"
                  checked={productIsSimpleProduct ? true : productUseManualPricing}
                  onCheckedChange={productIsSimpleProduct ? undefined : ((checked) => setProductUseManualPricing(checked === true))}
                  disabled={productIsSimpleProduct}
                />
                <div className="grid gap-1.5">
                  <Label
                    htmlFor="prod-manual-pricing"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Usar preço manual {productIsSimpleProduct && <span className="text-red-500">*</span>}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {productIsSimpleProduct
                      ? 'Produtos simples sempre usam preço manual (obrigatório).'
                      : 'Quando marcado, este produto não terá cálculo automático de preço baseado em materiais e margens.'}
                  </p>
                </div>
              </div>

              {/* Manual Price Input - shown when checkbox is checked or for simple products */}
              {(productUseManualPricing || productIsSimpleProduct) && (
                <div className="mt-4 grid gap-2">
                  <Label htmlFor="prod-manual-price">Preço Manual (R$)</Label>
                  <Input
                    id="prod-manual-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={productManualPrice}
                    onChange={(e) => setProductManualPrice(e.target.value)}
                    placeholder="Ex: 150.00"
                    required={productIsSimpleProduct || productUseManualPricing}
                  />
                  <p className="text-xs text-muted-foreground">
                    {productIsSimpleProduct
                      ? 'Preço unitário do produto. Campo obrigatório para produtos simples.'
                      : 'Este será o preço base do produto. Pode ser ajustado por dimensão se necessário.'}
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveProduct} disabled={medals.saving}>
              {medals.saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Dialog */}
      <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedMaterial ? 'Editar Material' : 'Novo Material'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do material acrílico
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="mat-code">Código</Label>
                <Input
                  id="mat-code"
                  value={materialCode}
                  onChange={(e) => setMaterialCode(e.target.value)}
                  placeholder="ACR-CRISTAL"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="mat-name">Nome</Label>
                <Input
                  id="mat-name"
                  value={materialName}
                  onChange={(e) => setMaterialName(e.target.value)}
                  placeholder="Acrílico Cristal"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="mat-density">Densidade (kg/m³)</Label>
                <Input
                  id="mat-density"
                  type="number"
                  step="0.01"
                  value={materialDensity}
                  onChange={(e) => setMaterialDensity(e.target.value)}
                  placeholder="1190"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="mat-price">Preço por Kg (R$)</Label>
                <Input
                  id="mat-price"
                  type="number"
                  step="0.01"
                  value={materialPricePerKg}
                  onChange={(e) => setMaterialPricePerKg(e.target.value)}
                  placeholder="35.00"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="mat-thicknesses">Espessuras Disponíveis (mm)</Label>
              <Input
                id="mat-thicknesses"
                value={materialAvailableThicknesses}
                onChange={(e) => setMaterialAvailableThicknesses(e.target.value)}
                placeholder="2, 3, 4, 5, 6, 8, 10"
              />
              <p className="text-xs text-muted-foreground">
                Separe as espessuras por vírgula (ex: 2, 3, 5, 8, 10)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={materialActive}
                onCheckedChange={setMaterialActive}
              />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaterialDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveMaterial} disabled={medals.saving}>
              {medals.saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ribbon Type Dialog */}
      <Dialog open={ribbonTypeDialogOpen} onOpenChange={setRibbonTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedRibbonType ? 'Editar Tipo de Fita' : 'Novo Tipo de Fita'}
            </DialogTitle>
            <DialogDescription>
              Configure o tipo de fita (lisa ou personalizada)
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="ribbon-type-code">Código</Label>
                <Input
                  id="ribbon-type-code"
                  value={ribbonTypeCode}
                  onChange={(e) => setRibbonTypeCode(e.target.value)}
                  placeholder="LISA"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ribbon-type-name">Nome</Label>
                <Input
                  id="ribbon-type-name"
                  value={ribbonTypeName}
                  onChange={(e) => setRibbonTypeName(e.target.value)}
                  placeholder="Fita Lisa"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ribbon-type-desc">Descrição (opcional)</Label>
              <Input
                id="ribbon-type-desc"
                value={ribbonTypeDescription}
                onChange={(e) => setRibbonTypeDescription(e.target.value)}
                placeholder="Descrição do tipo de fita"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={ribbonTypeIsPersonalized}
                  onCheckedChange={setRibbonTypeIsPersonalized}
                />
                <Label>Personalizada</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={ribbonTypeActive}
                  onCheckedChange={setRibbonTypeActive}
                />
                <Label>Ativo</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRibbonTypeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveRibbonType} disabled={ribbonSaving}>
              {ribbonSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ribbon Style Dialog */}
      <Dialog open={ribbonStyleDialogOpen} onOpenChange={setRibbonStyleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedRibbonStyle ? 'Editar Estilo de Fita' : 'Novo Estilo de Fita'}
            </DialogTitle>
            <DialogDescription>
              Configure o estilo e preço base da fita
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="ribbon-style-code">Código</Label>
                <Input
                  id="ribbon-style-code"
                  value={ribbonStyleCode}
                  onChange={(e) => setRibbonStyleCode(e.target.value)}
                  placeholder="SIMPLES"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ribbon-style-name">Nome</Label>
                <Input
                  id="ribbon-style-name"
                  value={ribbonStyleName}
                  onChange={(e) => setRibbonStyleName(e.target.value)}
                  placeholder="Simples Cordão"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="ribbon-style-price">Preço Base (R$)</Label>
                <Input
                  id="ribbon-style-price"
                  type="number"
                  step="0.01"
                  value={ribbonStyleBasePrice}
                  onChange={(e) => setRibbonStyleBasePrice(e.target.value)}
                  placeholder="3.50"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={ribbonStyleActive}
                  onCheckedChange={setRibbonStyleActive}
                />
                <Label>Ativo</Label>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ribbon-style-desc">Descrição (opcional)</Label>
              <Input
                id="ribbon-style-desc"
                value={ribbonStyleDescription}
                onChange={(e) => setRibbonStyleDescription(e.target.value)}
                placeholder="Descrição do estilo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRibbonStyleDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveRibbonStyle} disabled={ribbonSaving}>
              {ribbonSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ribbon Color Dialog */}
      <Dialog open={ribbonColorDialogOpen} onOpenChange={setRibbonColorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedRibbonColor ? 'Editar Cor de Fita' : 'Nova Cor de Fita'}
            </DialogTitle>
            <DialogDescription>
              Configure a cor e ajuste de preço
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="ribbon-color-code">Código</Label>
                <Input
                  id="ribbon-color-code"
                  value={ribbonColorCode}
                  onChange={(e) => setRibbonColorCode(e.target.value)}
                  placeholder="AZUL"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ribbon-color-name">Nome</Label>
                <Input
                  id="ribbon-color-name"
                  value={ribbonColorName}
                  onChange={(e) => setRibbonColorName(e.target.value)}
                  placeholder="Azul"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="ribbon-color-hex">Cor (Hex)</Label>
                <div className="flex gap-2">
                  <Input
                    id="ribbon-color-hex"
                    type="color"
                    value={ribbonColorHex}
                    onChange={(e) => setRibbonColorHex(e.target.value)}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={ribbonColorHex}
                    onChange={(e) => setRibbonColorHex(e.target.value)}
                    placeholder="#0000FF"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ribbon-color-adj">Ajuste de Preço (R$)</Label>
                <Input
                  id="ribbon-color-adj"
                  type="number"
                  step="0.01"
                  value={ribbonColorPriceAdjustment}
                  onChange={(e) => setRibbonColorPriceAdjustment(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={ribbonColorActive}
                onCheckedChange={setRibbonColorActive}
              />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRibbonColorDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveRibbonColor} disabled={ribbonSaving}>
              {ribbonSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
